import React, { useMemo } from 'react';

import { EventInfo, isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import { CarouselEventHud } from '@/components/event-hud/CarouselEventHud';
import { EventHudDetails, HudEvent } from '@/components/event-hud/EventHudDetails';
import { EventHudScoreboard } from '@/components/event-hud/EventHudScoreboard';
import { getPrematchHudHeight } from '@/components/event-hud/utils';
import { Box } from '@/components/lib/components';
import { ScoreboardTeamOverride } from '@/components/scoreboard/types';
import { sportSupportsScoreboard } from '@/components/scoreboard/use-scoreboard';
import { PICKS_USE_EVENT_STALE_TIME, useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { Event } from '@/feature/event-details-sbk/types';
import { HUD_HEIGHTS } from '@/feature/event-details-sbk/utils/get-hud-height';
import { EventHudPlayerIcon } from '@/feature/lobby/components/EventHudPlayerIcon';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { designSystem } from '@/styles/styles';
import { PlayerAttributesType, Sport } from '@/types/api.generated';
import { findPlayerAttribute } from '@/utils/fantasy-attribute-utils';
import { formatPlayerName } from '@/utils/format-player-name';
import { gameUtils } from '@/utils/games';
import '@/utils/league';
import { isTennis } from '@/utils/league';
import { isStandaloneScoreboardSport } from '@/utils/sport-utils';
import { getTennisMatchup, orderTennisMatchupByPamSides } from '@/utils/tennis-matchup';

type PickemEventHudProps = {
    event?: EventInfo;
};

const pickemSportToSbkSportMap = {
    [Sport.AmericanFootball]: 'American Football',
    [Sport.Baseball]: '',
    [Sport.Basketball]: '',
    [Sport.Golf]: '',
    [Sport.Mma]: '',
    [Sport.Hockey]: '',
    [Sport.Soccer]: '',
    [Sport.Esports]: '',
    [Sport.Tennis]: 'Tennis',
} as const;

export const PickemEventHud = ({ event: pickemEvent }: PickemEventHudProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;
    const isLive = pickemEvent && gameUtils.isLive(pickemEvent);
    const showScoreboard = useEventSupportsScoreboard(pickemEvent);

    // moved the call up from PickemEventCarouselHud, because for tennis we need the PAM ordering
    // for live events. for pre-game we use the normal ordering from pickem
    const { data: pamEvent } = useEvent(pamEventId ?? '', {
        enabled: !!pamEventId && !!isLive,
        staleTime: PICKS_USE_EVENT_STALE_TIME,
    });

    const hudEvent = useMemo(() => convertPickemEventToHudEvent(pickemEvent, pamEvent), [pickemEvent, pamEvent]);

    const teamOverride: ScoreboardTeamOverride | undefined = useMemo(() => {
        if (!hudEvent) {
            return undefined;
        }
        if (isTennis(hudEvent.league)) {
            return {
                logoVariant: 'player',
                icons: { away: hudEvent.awayTeam.logoUrl, home: hudEvent.homeTeam.logoUrl },
            };
        }
        return { icons: { away: hudEvent.awayTeam.logoUrl, home: hudEvent.homeTeam.logoUrl } };
    }, [hudEvent]);

    if (!hudEvent) {
        return null;
    }

    return showScoreboard && pamEventId && isLive ? (
        //Only show the event carousel hud for live events and when the scoreboard is enabled
        <PickemEventCarouselHud
            pamEvent={pamEvent}
            pamEventId={pamEventId}
            hudEvent={hudEvent}
            teamOverride={teamOverride}
        />
    ) : (
        //If the event is not LIVE, pass undefined so we dont fetch data un-necesarily
        <Box height={getPrematchHudHeight(hudEvent)}>
            <EventHudDetails event={hudEvent} pamEventId={isLive ? pamEventId : undefined} />
        </Box>
    );
};

/**
 * Event HUD fetching the PAM data, and display a carousel with the event score details,
 * and the scoreboard on the second slide.
 * If the event is not LIVE in PAM yet, fallback to the default hud component
 */
const PickemEventCarouselHud = ({
    pamEvent,
    pamEventId,
    hudEvent,
    teamOverride,
}: {
    pamEvent?: Event;
    pamEventId: string;
    hudEvent: HudEvent;
    teamOverride?: ScoreboardTeamOverride;
}) => {
    const bareHud = (
        <Box height={getPrematchHudHeight(hudEvent)}>
            <EventHudDetails event={hudEvent} pamEventId={pamEventId} />
        </Box>
    );

    // Until PAM reports the event live, fall back to the plain HUD.
    if (pamEvent?.status !== 'LIVE') {
        return bareHud;
    }
    // A sport without a builder renders an empty scoreboard (e.g. esports), so fall back to the plain
    // HUD — same gate SBK's EventHud applies upstream via useSbkHudHeight.
    if (!sportSupportsScoreboard(pamEvent.sport)) {
        return bareHud;
    }
    // Mirror SBK's EventHud: modern-variant sports (tennis) render the scoreboard alone with no
    // HUD-Live carousel slide; classic-variant sports keep the carousel.
    if (isStandaloneScoreboardSport(hudEvent.sport)) {
        return (
            <Box height={HUD_HEIGHTS.liveScoreboardHeader}>
                <EventHudScoreboard pamEventId={pamEventId} teamOverride={teamOverride} />
            </Box>
        );
    }
    return (
        <Box height={HUD_HEIGHTS.liveHeader}>
            <CarouselEventHud event={hudEvent} pamEventId={pamEventId} teamOverride={teamOverride} />
        </Box>
    );
};

/**
 * `pamEvent` (when available) orders tennis players by PAM's away/home sides, so the side-keyed
 * scores and photos rendered against the hud event pair up with the right player.
 */
export const convertPickemEventToHudEvent = (event?: EventInfo, pamEvent?: Event): HudEvent | null => {
    if (isTennis(event?.league) && isIndividualEvent(event)) {
        const [player1, player2] = orderTennisMatchupByPamSides(getTennisMatchup(event.players, event.name), pamEvent);
        const player1Rank = findPlayerAttribute(player1.player, PlayerAttributesType.TennisRank);
        const player2Rank = findPlayerAttribute(player2.player, PlayerAttributesType.TennisRank);
        return {
            startTime: new Date(event.date).getTime(),
            sport: pickemSportToSbkSportMap[event.sport] || '',
            awayTeam: {
                logoComponent: <EventHudPlayerIcon playerData={player1.player} />,
                logoUrl: player1.player?.icon,
                shortCode: formatPlayerName(player1),
                record: player1Rank ? `#${player1Rank}` : undefined,
            },
            homeTeam: {
                logoComponent: <EventHudPlayerIcon playerData={player2.player} />,
                logoUrl: player2.player?.icon,
                shortCode: formatPlayerName(player2),
                record: player2Rank ? `#${player2Rank}` : undefined,
            },
            league: event.league,
        };
    }

    if (!isTeamEvent(event)) {
        return null;
    }

    return {
        startTime: new Date(event.date).getTime(),
        // the component handles some sports differently
        // this component comes from SBK and has a different sport type
        // we need to map our sport to the SBK sport type
        sport: pickemSportToSbkSportMap[event.sport] || '',
        awayTeam: {
            alias: event.teams[0].name,
            logoUrl: event.teams[0].largeIcon,
            primaryColor: event.teams[0].color || designSystem.colors.neutral300,
            secondaryColor: event.teams[0].secondaryColor || designSystem.colors.neutral900,
            shortCode: event.teams[0].name,
        },
        homeTeam: {
            alias: event.teams[1].name,
            logoUrl: event.teams[1].largeIcon,
            primaryColor: event.teams[1].color || designSystem.colors.neutral300,
            secondaryColor: event.teams[1].secondaryColor || designSystem.colors.neutral900,
            shortCode: event.teams[1].name,
        },
        league: event.league,
    };
};
